import { type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  Target,
  ListChecks,
  Waypoints,
  Ban,
  Newspaper,
  Library,
  CalendarClock,
  AlertTriangle,
  ArrowRight,
  type LucideIcon,
} from 'lucide-react';
import {
  Badge,
  Card,
  CardBody,
  EmptyState,
  PageHeader,
  Spinner,
  type BadgeTone,
} from '../../ui';
import { useTeamToday } from '../../features/teamToday/useTeamToday';
import { stageLabel } from '../../features/workstreams/workstreamConfig';
import { stageTone } from '../../features/workstreams/workstreamModel';
import type { UpcomingMilestone } from '../../features/teamToday/types';

interface TileProps {
  icon: LucideIcon;
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  onClick?: () => void;
}

function Tile({ icon: Icon, label, value, hint, onClick }: TileProps) {
  return (
    <Card interactive={!!onClick} onClick={onClick} className="min-w-0">
      <CardBody className="flex items-center gap-4">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-surface-3 text-accent">
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <div className="text-2xl font-semibold tabular-nums text-zinc-50">
            {value}
          </div>
          <div className="truncate text-xs text-zinc-500">
            {label}
            {hint ? <span className="text-zinc-600"> · {hint}</span> : null}
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

/** Badge for a milestone's timing. */
function MilestoneTiming({ milestone }: { milestone: UpcomingMilestone }) {
  let tone: BadgeTone = 'neutral';
  let label: string;
  if (milestone.overdue) {
    tone = 'danger';
    label = `${Math.abs(milestone.daysUntil)}d overdue`;
  } else if (milestone.daysUntil === 0) {
    tone = 'warning';
    label = 'due today';
  } else if (milestone.daysUntil <= 7) {
    tone = 'warning';
    label = `in ${milestone.daysUntil}d`;
  } else {
    tone = 'info';
    label = `in ${milestone.daysUntil}d`;
  }
  return <Badge tone={tone}>{label}</Badge>;
}

/**
 * "Who is working on what?" — the one element of Team Today worth preserving
 * per the UX audit. Self-contained (reads {@link useTeamToday}) so the unified
 * Home page can compose it into its "Team" tab alongside {@link
 * TeamVectorContent} without re-duplicating the tiles/milestones that Team
 * Vector already shows.
 */
export function WhoIsWorkingPanel() {
  const navigate = useNavigate();
  const { snapshot, loading, error } = useTeamToday();
  const { whoIsWorking } = snapshot;

  return (
    <Card>
      <CardBody>
        <h2 className="mb-3 flex items-center gap-1.5 text-sm font-semibold text-zinc-100">
          <Users className="h-4 w-4 text-accent" aria-hidden="true" />
          Who is working on what?
        </h2>
        {loading ? (
          <div className="flex items-center justify-center py-10">
            <Spinner label="Loading team activity" />
          </div>
        ) : error ? (
          <p className="py-8 text-center text-sm text-zinc-500" role="alert">
            {error}
          </p>
        ) : whoIsWorking.length === 0 ? (
          <p className="py-8 text-center text-sm text-zinc-500">
            No active workstreams yet — create one so the team can see what's in
            flight.
          </p>
        ) : (
          <div className="space-y-4">
            {whoIsWorking.map((group) => (
              <div key={group.engineer}>
                <div className="mb-1.5 flex items-center gap-1.5 text-xs font-medium text-zinc-400">
                  <Users className="h-3.5 w-3.5" aria-hidden="true" />
                  {group.engineer}
                </div>
                <div className="space-y-1">
                  {group.items.map((item) => (
                    <button
                      key={item.workstreamId}
                      type="button"
                      onClick={() =>
                        navigate(
                          `/workstreams/${encodeURIComponent(item.workstreamId)}`,
                        )
                      }
                      className="group flex w-full items-center gap-2.5 rounded-xl border border-transparent px-3 py-2 text-left transition-colors hover:border-surface-border hover:bg-surface-2"
                    >
                      <span className="min-w-0 flex-1 truncate text-sm text-zinc-200">
                        {item.title}
                      </span>
                      {item.stage && (
                        <Badge tone={stageTone(item.stage)} dot>
                          {stageLabel(item.stage)}
                        </Badge>
                      )}
                      {item.goalTitle && (
                        <span className="hidden max-w-[10rem] items-center gap-1 truncate text-xs text-zinc-500 sm:inline-flex">
                          <Target className="h-3 w-3 shrink-0" aria-hidden="true" />
                          {item.goalTitle}
                        </span>
                      )}
                      <ArrowRight className="h-4 w-4 shrink-0 text-zinc-600 transition-colors group-hover:text-zinc-300" />
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardBody>
    </Card>
  );
}

/**
 * Team Today — the team operating & awareness dashboard. Summary tiles roll up
 * goal health, sprint progress, active workstreams, open blockers (+ forecast
 * impact), recent changes and upcoming milestones; the "who is working on
 * what?" list maps every engineer to their active workstream → stage → goal.
 *
 * Pure awareness: no productivity/velocity/ranking — just what's in flight,
 * what's at risk, and what's coming up.
 */
export function TeamTodayPage() {
  const navigate = useNavigate();
  const { snapshot, loading, error } = useTeamToday();
  const { summary, upcomingMilestones, whoIsWorking } = snapshot;

  const goalsHint = [
    summary.goalsAtRisk > 0 ? `${summary.goalsAtRisk} at risk` : null,
    summary.goalsOffTrack > 0 ? `${summary.goalsOffTrack} off track` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  let body: ReactNode;
  if (loading) {
    body = (
      <div className="flex items-center justify-center py-24">
        <Spinner label="Loading Team Today" />
      </div>
    );
  } else if (error) {
    body = (
      <EmptyState
        icon={<AlertTriangle className="h-6 w-6" />}
        title="Couldn't load Team Today"
        description={error}
      />
    );
  } else {
    body = (
      <div className="space-y-6">
        {/* Summary tiles. */}
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
          <Tile
            icon={Target}
            label="Goals on track"
            value={summary.goalsOnTrack}
            hint={goalsHint || `${summary.totalGoals} total`}
            onClick={() => navigate('/goals')}
          />
          <Tile
            icon={ListChecks}
            label="Sprint progress"
            value={
              summary.sprintCompletionPct === undefined
                ? '—'
                : `${summary.sprintCompletionPct}%`
            }
            hint={
              summary.sprintTotal > 0
                ? `${summary.sprintDone}/${summary.sprintTotal} done`
                : 'no tasks'
            }
            onClick={() => navigate('/tasks')}
          />
          <Tile
            icon={Waypoints}
            label="Active workstreams"
            value={summary.activeWorkstreams}
            onClick={() => navigate('/workstreams')}
          />
          <Tile
            icon={Ban}
            label="Open blockers"
            value={summary.openBlockers}
            hint={
              summary.blockerForecastImpactDays > 0
                ? `~${summary.blockerForecastImpactDays}d forecast impact`
                : undefined
            }
            onClick={() => navigate('/blockers')}
          />
          <Tile
            icon={Newspaper}
            label="Recent changes"
            value={summary.recentChanges}
            hint="last 7 days"
            onClick={() => navigate('/changes')}
          />
          <Tile
            icon={Library}
            label="New documents"
            value={summary.newDocuments}
            hint="last 7 days"
            onClick={() => navigate('/documents')}
          />
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          {/* Who is working on what. */}
          <Card className="lg:col-span-2">
            <CardBody>
              <h2 className="mb-3 text-sm font-semibold text-zinc-100">
                Who is working on what?
              </h2>
              {whoIsWorking.length === 0 ? (
                <p className="py-8 text-center text-sm text-zinc-500">
                  No active workstreams yet — create one so the team can see
                  what's in flight.
                </p>
              ) : (
                <div className="space-y-4">
                  {whoIsWorking.map((group) => (
                    <div key={group.engineer}>
                      <div className="mb-1.5 flex items-center gap-1.5 text-xs font-medium text-zinc-400">
                        <Users className="h-3.5 w-3.5" aria-hidden="true" />
                        {group.engineer}
                      </div>
                      <div className="space-y-1">
                        {group.items.map((item) => (
                          <button
                            key={item.workstreamId}
                            type="button"
                            onClick={() =>
                              navigate(
                                `/workstreams/${encodeURIComponent(item.workstreamId)}`,
                              )
                            }
                            className="group flex w-full items-center gap-2.5 rounded-xl border border-transparent px-3 py-2 text-left transition-colors hover:border-surface-border hover:bg-surface-2"
                          >
                            <span className="min-w-0 flex-1 truncate text-sm text-zinc-200">
                              {item.title}
                            </span>
                            {item.stage && (
                              <Badge tone={stageTone(item.stage)} dot>
                                {stageLabel(item.stage)}
                              </Badge>
                            )}
                            {item.goalTitle && (
                              <span className="hidden max-w-[10rem] items-center gap-1 truncate text-xs text-zinc-500 sm:inline-flex">
                                <Target className="h-3 w-3 shrink-0" aria-hidden="true" />
                                {item.goalTitle}
                              </span>
                            )}
                            <ArrowRight className="h-4 w-4 shrink-0 text-zinc-600 transition-colors group-hover:text-zinc-300" />
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardBody>
          </Card>

          {/* Upcoming milestones. */}
          <Card>
            <CardBody>
              <h2 className="mb-3 flex items-center gap-1.5 text-sm font-semibold text-zinc-100">
                <CalendarClock className="h-4 w-4 text-accent" aria-hidden="true" />
                Upcoming milestones
              </h2>
              {upcomingMilestones.length === 0 ? (
                <p className="py-8 text-center text-sm text-zinc-500">
                  No goal milestones scheduled.
                </p>
              ) : (
                <div className="space-y-1">
                  {upcomingMilestones.map((m) => (
                    <button
                      key={`${m.goalId}:${m.label}:${m.dueDate}`}
                      type="button"
                      onClick={() => navigate(`/goals/${encodeURIComponent(m.goalId)}`)}
                      className="group flex w-full items-start gap-2 rounded-xl border border-transparent px-3 py-2 text-left transition-colors hover:border-surface-border hover:bg-surface-2"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium text-zinc-200">
                          {m.label}
                        </div>
                        <div className="truncate text-xs text-zinc-500">
                          {m.goalTitle}
                        </div>
                      </div>
                      <MilestoneTiming milestone={m} />
                    </button>
                  ))}
                </div>
              )}
            </CardBody>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        icon={<Users className="h-5 w-5" />}
        title="Team Today"
        description="A single glance at what the team is doing, what's at risk, and what's coming up."
      />
      {body}
    </div>
  );
}

export default TeamTodayPage;
